import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Collapse, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography,
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import {
    IPayrollVariance, IPayrollVarianceChange, IPayrollVariancePerson,
} from '../../interfaces/payroll.interface';
import { formatMoney } from '../../utils/format';
import { formatDateTime } from '../../utils/dateUtils';

interface Props {
    institutionId: string;
    year: number;
    month: number;
    /** Changes whenever the run is reloaded, so an open panel refetches. */
    refreshKey?: unknown;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December'];
const monthLabel = (year: number, month: number) => `${MONTHS[month - 1] ?? month} ${year}`;

const CHANGE_LABEL: Record<IPayrollVarianceChange['kind'], string> = {
    ROW_ADDED: 'Payroll row added',
    ROW_EDITED: 'Payroll row saved',
    ROW_REMOVED: 'Payroll row removed',
    EMPLOYEE_EDITED: 'Employee user account saved (bank, wallet, phone, password or sign-in settings)',
};

/** "+৳ 1,000.00", "−৳ 500.00" or "no change". */
const signedMoney = (n: number | null | undefined): string => {
    if (n === null || n === undefined) return '';
    if (n === 0) return 'no change';
    return `${n > 0 ? '+' : '−'}${formatMoney(Math.abs(n), 'BDT')}`;
};
const signedCount = (n: number | null | undefined): string => {
    if (n === null || n === undefined) return '';
    if (n === 0) return 'no change';
    return `${n > 0 ? '+' : '−'}${Math.abs(n)}`;
};

const tileSx = { flex: '1 1 160px', maxWidth: 320, minWidth: 0 } as const;

interface TileProps {
    testId: string; label: string; value: string; previous?: string; difference?: string;
}
const Tile: React.FC<TileProps> = ({ testId, label, value, previous, difference }) => (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }} data-testid={testId}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
        {previous !== undefined && (
            <Typography variant="caption" color="text.secondary" display="block">{previous}</Typography>
        )}
        {difference !== undefined && difference !== '' && (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{difference}</Typography>
        )}
    </Paper>
);

const PeopleList: React.FC<{
    testId: string; title: string; count: number; people: IPayrollVariancePerson[];
    emptyText: string; netLabel: string;
}> = ({ testId, title, count, people, emptyText, netLabel }) => (
    <Box data-testid={testId} sx={{ mb: 2 }}>
        <Typography variant="subtitle2">{title} ({count})</Typography>
        {people.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
        ) : (
            <>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {people.map((p) => (
                        <li key={p.userId}>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {p.name}{p.employeeCode ? ` (${p.employeeCode})` : ''}: {formatMoney(p.net, 'BDT')} {netLabel}
                            </Typography>
                        </li>
                    ))}
                </Box>
                {count > people.length && (
                    <Typography variant="caption" color="text.secondary">
                        Showing the first {people.length} of {count}.
                    </Typography>
                )}
            </>
        )}
    </Box>
);

/**
 * B-42: "What changed since last month" for the payroll approver. Collapsed by default; the comparison is
 * fetched the first time it is opened. Read-only information, no effect on the approval rules.
 */
const PayrollVariancePanel: React.FC<Props> = ({ institutionId, year, month, refreshKey }) => {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState<IPayrollVariance | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    // Counts requests so that a slow answer for an earlier month is ignored.
    const requestId = useRef(0);

    const load = useCallback(async () => {
        const mine = ++requestId.current;
        setLoading(true);
        setFailed(false);
        try {
            const result = await PayrollService.getRunVariance(year, month, institutionId);
            if (mine === requestId.current) setData(result);
        } catch {
            if (mine === requestId.current) {
                setData(null);
                setFailed(true);
            }
        } finally {
            if (mine === requestId.current) setLoading(false);
        }
    }, [institutionId, year, month]);

    // A different month or a reload makes the old comparison stale.
    useEffect(() => {
        requestId.current += 1;
        setData(null);
        setFailed(false);
        setLoading(false);
    }, [institutionId, year, month, refreshKey]);
    useEffect(() => { if (open && !data && !loading && !failed) load(); }, [open, data, loading, failed, load]);

    const prevLabel = data ? monthLabel(data.previousYear, data.previousMonth) : '';

    return (
        <Box data-help-section="payroll.run.variance" sx={{ mb: 2 }} data-testid="payroll-variance-panel">
            <Button
                size="small" variant="outlined" onClick={() => setOpen((o) => !o)}
                aria-expanded={open} data-help="payroll.run.variance.toggle"
            >
                {open ? 'Hide: what changed since last month' : 'What changed since last month'}
            </Button>
            <Collapse in={open} unmountOnExit>
                <Paper variant="outlined" sx={{ mt: 1, p: 2 }}>
                    {loading && <Typography variant="body2">Loading the comparison...</Typography>}
                    {failed && (
                        <Alert severity="warning" action={<Button data-help="payroll.variance.retry" size="small" color="inherit" onClick={load}>Try again</Button>}>
                            The comparison could not be loaded.
                        </Alert>
                    )}
                    {data && (
                        <>
                            {!data.previous && (
                                <Alert severity="info" sx={{ mb: 2 }} data-testid="payroll-variance-no-previous">
                                    There is no payroll for {prevLabel} to compare with. Only this month's figures are shown.
                                </Alert>
                            )}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                                <Box data-help="payroll.run.variance.tile.net" sx={tileSx}><Tile testId="variance-tile-net"
                                      label="Net pay" value={formatMoney(data.current.net, 'BDT')}
                                      previous={data.previous ? `${prevLabel}: ${formatMoney(data.previous.net, 'BDT')}` : undefined}
                                      difference={signedMoney(data.netDifference)} /></Box>
                                <Box data-help="payroll.run.variance.tile.gross" sx={tileSx}><Tile testId="variance-tile-gross"
                                      label="Gross pay" value={formatMoney(data.current.gross, 'BDT')}
                                      previous={data.previous ? `${prevLabel}: ${formatMoney(data.previous.gross, 'BDT')}` : undefined}
                                      difference={signedMoney(data.grossDifference)} /></Box>
                                <Box data-help="payroll.run.variance.tile.headcount" sx={tileSx}><Tile testId="variance-tile-headcount"
                                      label="Workers" value={String(data.current.headcount)}
                                      previous={data.previous ? `${prevLabel}: ${data.previous.headcount}` : undefined}
                                      difference={signedCount(data.headcountDifference)} /></Box>
                            </Box>

                            {data.payoutChecks && (
                                <Box data-help="payroll.run.variance.checks" data-testid="variance-payout-checks" sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2">
                                        Payout checks ({data.payoutChecks.sharedNumberGroupCount + data.payoutChecks.noPayoutMethodCount + (data.payoutChecks.chequeCount ?? 0)})
                                    </Typography>
                                    {data.payoutChecks.sharedNumberGroupCount === 0 && data.payoutChecks.noPayoutMethodCount === 0 && !data.payoutChecks.chequeCount && (
                                        <Typography variant="body2" color="text.secondary" data-testid="variance-payout-checks-clear">
                                            No shared payout numbers, no missing payout methods and no cheque payments in this run.
                                        </Typography>
                                    )}
                                    {data.payoutChecks.sharedNumbers.length > 0 && (
                                        <Alert severity="warning" sx={{ mt: 0.5, mb: 1 }} data-testid="variance-shared-numbers">
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                Shared payout number ({data.payoutChecks.sharedNumberGroupCount})
                                            </Typography>
                                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                                {data.payoutChecks.sharedNumbers.map((g) => (
                                                    <li key={`${g.kind}-${g.maskedNumber}-${g.employees.map((e) => e.userId).join(',')}`}>
                                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                            {g.message}: {g.kind === 'WALLET' ? 'mobile wallet' : 'bank account'} {g.maskedNumber}.{' '}
                                                            {g.employees.map((e) => `${e.name}${e.employeeCode ? ` (${e.employeeCode})` : ''}`).join(', ')}
                                                        </Typography>
                                                    </li>
                                                ))}
                                            </Box>
                                            {data.payoutChecks.sharedNumberGroupCount > data.payoutChecks.sharedNumbers.length && (
                                                <Typography variant="caption" display="block">
                                                    Showing the first {data.payoutChecks.sharedNumbers.length} of {data.payoutChecks.sharedNumberGroupCount}.
                                                </Typography>
                                            )}
                                        </Alert>
                                    )}
                                    {data.payoutChecks.noPayoutMethod.length > 0 && (
                                        <Alert severity="warning" sx={{ mt: 0.5, mb: 1 }} data-testid="variance-no-payout">
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                No payout method ({data.payoutChecks.noPayoutMethodCount})
                                            </Typography>
                                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                                {data.payoutChecks.noPayoutMethod.map((p) => (
                                                    <li key={p.userId}>
                                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                            {p.name}{p.employeeCode ? ` (${p.employeeCode})` : ''}: {p.reason}
                                                        </Typography>
                                                    </li>
                                                ))}
                                            </Box>
                                            {data.payoutChecks.noPayoutMethodCount > data.payoutChecks.noPayoutMethod.length && (
                                                <Typography variant="caption" display="block">
                                                    Showing the first {data.payoutChecks.noPayoutMethod.length} of {data.payoutChecks.noPayoutMethodCount}.
                                                </Typography>
                                            )}
                                        </Alert>
                                    )}
                                    {(data.payoutChecks.chequeEmployees?.length ?? 0) > 0 && (
                                        <Alert severity="info" sx={{ mt: 0.5, mb: 1 }} data-testid="variance-cheque">
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                Paid by cheque ({data.payoutChecks.chequeCount})
                                            </Typography>
                                            <Typography variant="body2">A cheque must be written and signed for each of these employees.</Typography>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                {data.payoutChecks.chequeEmployees!.map((e) => `${e.name}${e.employeeCode ? ` (${e.employeeCode})` : ''}`).join(', ')}
                                            </Typography>
                                            {(data.payoutChecks.chequeCount ?? 0) > data.payoutChecks.chequeEmployees!.length && (
                                                <Typography variant="caption" display="block">
                                                    Showing the first {data.payoutChecks.chequeEmployees!.length} of {data.payoutChecks.chequeCount}.
                                                </Typography>
                                            )}
                                        </Alert>
                                    )}
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        These are warnings only. They do not stop approval. Numbers are partly hidden. Employees paid by cash are not checked.
                                    </Typography>
                                </Box>
                            )}

                            {data.previous && (
                                <>
                                    <Box data-help="payroll.run.variance.joiners"><PeopleList testId="variance-joiners"
                                                title={`New this month`} count={data.joinerCount} people={data.joiners}
                                                emptyText={`Nobody is new compared with ${prevLabel}.`} netLabel="net" /></Box>
                                    <Box data-help="payroll.run.variance.leavers"><PeopleList testId="variance-leavers"
                                                title={`Not in this month`} count={data.leaverCount} people={data.leavers}
                                                emptyText={`Everyone paid in ${prevLabel} is in this month.`} netLabel="net last month" /></Box>

                                    <Box data-help="payroll.run.variance.payout" data-testid="variance-payout" sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2">Bank or wallet changed ({data.payoutChangeCount})</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {data.payoutComparisonNote} Compared: {data.payoutComparedCount} workers.
                                        </Typography>
                                        {data.payoutChanges.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" data-testid="variance-payout-empty">
                                                {data.payoutComparedCount === 0
                                                    ? 'Last month has no bank or wallet payments recorded, so nothing could be compared.'
                                                    : 'No bank or wallet details differ from last month\'s payment.'}
                                            </Typography>
                                        ) : (
                                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                                {data.payoutChanges.map((c) => (
                                                    <li key={`${c.userId}-${c.kind}`}>
                                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                            {c.name}{c.employeeCode ? ` (${c.employeeCode})` : ''}:{' '}
                                                            {c.kind === 'METHOD' ? 'payment method' : c.kind === 'WALLET' ? 'mobile wallet' : 'bank account'} changed from{' '}
                                                            {c.previousMasked ?? 'none'} to {c.currentMasked ?? 'none'}
                                                            {c.kind !== 'METHOD' && c.previousMasked !== null && c.previousMasked === c.currentMasked
                                                                ? ' (other digits changed)' : ''}
                                                        </Typography>
                                                    </li>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>

                                    <Box data-help="payroll.run.variance.biggest" data-testid="variance-biggest" sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2">Biggest changes in net pay</Typography>
                                        {data.biggestChanges.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                Nobody's net pay changed.
                                            </Typography>
                                        ) : (
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell data-help="payroll.run.variance.col.name">Worker</TableCell>
                                                            <TableCell data-help="payroll.run.variance.col.lastNet" align="right">Last month</TableCell>
                                                            <TableCell data-help="payroll.run.variance.col.thisNet" align="right">This month</TableCell>
                                                            <TableCell data-help="payroll.run.variance.col.diff" align="right">Change</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {data.biggestChanges.map((c) => (
                                                            <TableRow key={c.userId}>
                                                                <TableCell>{c.name}{c.employeeCode ? ` (${c.employeeCode})` : ''}</TableCell>
                                                                <TableCell align="right">{formatMoney(c.previousNet, 'BDT')}</TableCell>
                                                                <TableCell align="right">{formatMoney(c.currentNet, 'BDT')}</TableCell>
                                                                <TableCell align="right">{signedMoney(c.difference)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Box>
                                </>
                            )}

                            {data.afterApproval && (
                                <Box data-help="payroll.run.variance.afterApproval" data-testid="variance-after-approval">
                                    <Alert severity="warning" sx={{ mb: 1 }}>
                                        Changed after approval. Approved on {formatDateTime(data.afterApproval.approvedAt)}
                                        {data.afterApproval.approvedByName ? ` by ${data.afterApproval.approvedByName}` : ''}:{' '}
                                        {formatMoney(data.afterApproval.approvedNet, 'BDT')} net for {data.afterApproval.approvedHeadcount ?? 0} workers.
                                        Now: {formatMoney(data.afterApproval.currentNet, 'BDT')} net for {data.afterApproval.currentHeadcount} workers.
                                    </Alert>
                                    {data.afterApproval.changes.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            The system found no saved change to name. The totals or payout details no longer match the approval.
                                        </Typography>
                                    ) : (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell data-help="payroll.run.variance.col.change">What</TableCell>
                                                        <TableCell data-help="payroll.run.variance.col.name">Worker</TableCell>
                                                        <TableCell data-help="payroll.run.variance.col.when">When</TableCell>
                                                        <TableCell data-help="payroll.run.variance.col.by">Saved by</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {data.afterApproval.changes.map((c, i) => (
                                                        <TableRow key={`${c.kind}-${c.userId}-${i}`}>
                                                            <TableCell>{CHANGE_LABEL[c.kind]}</TableCell>
                                                            <TableCell>{c.name ?? '—'}{c.employeeCode ? ` (${c.employeeCode})` : ''}</TableCell>
                                                            <TableCell>{c.changedAt ? formatDateTime(c.changedAt) : '—'}{c.aroundApprovalTime ? ' (around approval time)' : ''}</TableCell>
                                                            <TableCell>{c.changedByName ?? 'Not recorded'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                    {data.afterApproval.changeCount > data.afterApproval.changes.length && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Showing the {data.afterApproval.changes.length} most recent of {data.afterApproval.changeCount}.
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                        {data.afterApproval.historyNote}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
            </Collapse>
        </Box>
    );
};

export default PayrollVariancePanel;
